import type { Store, Location as StoreLocation } from "@hobbyshopify/backend-client";

/**
 * The rules the hosted shop page has to satisfy before Stripe is given its URL,
 * shared by the onboarding step that claims a page and the settings section that
 * edits one — so the two can never disagree about what a usable page is.
 *
 * `ShopPageOps.isPublishable` on the server is the authority. This mirrors it so
 * a merchant is told what to fix while they are looking at the fields, rather
 * than discovering it weeks later when verification stalls. Opening hours are
 * absent on purpose: good for customers, not something a reviewer asks for.
 */

/**
 * What every shop on this platform sells, offered as a starting point rather
 * than a blank box: this is a trading-card product, so the generic answer is
 * true of the whole customer base and most merchants only want to add their own
 * specialities to it.
 *
 * Word-for-word the fallback `StripeOps.businessProfileFor` uses when a store
 * has no page at all, so a merchant who accepts the preset gets exactly what
 * Stripe would have been told anyway — only now on a page with their name,
 * address and returns policy around it.
 */
export const PRESET_DESCRIPTION = "Collectible trading cards, including singles, sealed product and graded cards.";

/** Every shop has a handle, so a filled-in page with no site of its own is what says the page was chosen. */
export const hostsShopPage = (store: Store | null | undefined): boolean =>
  Boolean(!store?.websiteUrl?.trim() && store?.contactEmail?.trim() && store?.returnsPolicy?.trim());

// Deliberately loose: the backend validates the address for real, and this only
// has to catch the typo that would otherwise come back as a 400.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function contactEmailError(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "Required: a business page has to say how to reach you.";
  if (!EMAIL_RE.test(trimmed)) return "Enter a valid email address.";
  return null;
}

export function returnsPolicyError(value: string): string | null {
  if (!value.trim()) return "Required: card networks expect a returns policy on a business website.";
  return null;
}

/**
 * Only ever reached by clearing a field that arrives filled in: the description
 * is asked for once, on step 1, with `PRESET_DESCRIPTION` already in it.
 */
export const DESCRIPTION_REQUIRED = "Say what you sell — a page without it reads as a shop that doesn't exist.";

// Every part the server joins into an address line. Presence of any one of them
// is what makes `formatAddress` return something rather than null, which is what
// `isPublishable` actually tests — so a branch known only by its city counts.
const ADDRESS_FIELDS = ["address1", "address2", "city", "province", "zip", "country"] as const;

/** Whether any location has an address the shop page can print. */
export const hasMappableAddress = (locations: readonly StoreLocation[]): boolean =>
  locations.some((location) => ADDRESS_FIELDS.some((field) => location[field]?.trim()));
